// src/features/dashboard/hooks/useAppList.ts

import {
  useEffect,
  useState,
} from 'react';

import { ENV } from '../../../config/env';

import {
  mapAppListData,
  productiveAppsMockData,
  nonProductiveAppsMockData,
} from '../mocks/appList.mock';

import {
  getAppList,
} from '../services/appListApi';

import type {
  AppListItem,
} from '../types/appList.types';

import {
  useManagerDate,
} from '../context/ManagerDateContext';

import {
  formatApiDate,
} from '../utils/date.utils';

export const useAppList = (
  ManagerEmployeeCode: string,
) => {

  /**
   * =========================================
   * MANAGER DATE CONTEXT
   * =========================================
   */
  const {
    fromDate,
    toDate,
  } = useManagerDate();

  /**
   * =========================================
   * STATE
   * =========================================
   */
  const [
    productiveApps,
    setProductiveApps,
  ] = useState<AppListItem[]>([]);

  const [
    nonProductiveApps,
    setNonProductiveApps,
  ] = useState<AppListItem[]>([]);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  /**
   * =========================================
   * LOAD APPS
   * =========================================
   */
  useEffect(() => {

    const loadApps =
      async () => {

        if (
          !ManagerEmployeeCode ||
          Number.isNaN(
            ManagerEmployeeCode,
          )
        ) {
          console.error(
            'Invalid ManagerEmployeeCode:',
            ManagerEmployeeCode,
          );

          return;
        }

        try {

          setLoading(true);

          setError('');

          /**
           * =========================================
           * MOCK MODE
           * =========================================
           */
          if (ENV.USE_MOCK) {

            setProductiveApps(
              mapAppListData(
                productiveAppsMockData,
              ),
            );

            setNonProductiveApps(
              mapAppListData(
                nonProductiveAppsMockData,
              ),
            );

            return;
          }

          /**
           * =========================================
           * PRODUCTIVE APPS PAYLOAD
           * =========================================
           */
          const productivePayload = {

            ManagerEmployeeCode,

            fromDate:
              formatApiDate(
                fromDate,
              ),

            toDate:
              formatApiDate(
                toDate,
              ),

            applicationType:
              'Productive',
          };

          /**
           * =========================================
           * NON-PRODUCTIVE APPS PAYLOAD
           * =========================================
           */
          const nonProductivePayload = {

            ManagerEmployeeCode,

            fromDate:
              formatApiDate(
                fromDate,
              ),

            toDate:
              formatApiDate(
                toDate,
              ),

            applicationType:
              'Non-Productive',
          };

          console.log(
            'Productive Apps Payload:',
            productivePayload,
          );

          console.log(
            'Non Productive Apps Payload:',
            nonProductivePayload,
          );

          /**
           * =========================================
           * API CALLS
           * =========================================
           */
          const [
            productiveResponse,
            nonProductiveResponse,
          ] = await Promise.all([
            getAppList(
              productivePayload,
            ),
            getAppList(
              nonProductivePayload,
            ),
          ]);

          /**
           * =========================================
           * MAP RESPONSE
           * =========================================
           */
          setProductiveApps(
            mapAppListData(
              productiveResponse,
            ),
          );

          setNonProductiveApps(
            mapAppListData(
              nonProductiveResponse,
            ),
          );

        } catch (err) {

          console.error(
            'Application list error:',
            err,
          );

          setError(
            'Failed to load application list',
          );

          setProductiveApps([]);

          setNonProductiveApps([]);

        } finally {

          setLoading(false);
        }
      };

    loadApps();

  }, [
    ManagerEmployeeCode,
    fromDate,
    toDate,
  ]);

  return {

    productiveApps,

    nonProductiveApps,

    loading,

    error,
  };
};